import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, InlineNotification, Modal, Pagination, RadioButton, RadioButtonGroup, Tag, TextArea } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { RISK_ACTION_LABEL, riskRuleLabel, type RiskAction } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError } from '../components/states';
import { Money, When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';

type Status = 'OPEN' | 'CLEARED' | 'FRAUD';
type RiskEvent = {
  id: string; source: 'game' | 'cms'; userId: number; nickname: string | null; username: string | null; rule: string;
  detail: Record<string, unknown>; related: number[]; relatedUsers: { id: number; nickname: string | null }[]; actions: RiskAction[];
  status: Status; at: string; resolvedAt: string | null; resolvedByName: string | null; note: string | null; clawedStake: string;
  withdrawFrozen: boolean;
};
type Page = { items: RiskEvent[]; total: number; counts: Partial<Record<Status, number>>; rules: Record<string, string> };

const STATUS: Record<Status, { label: string; type: 'red' | 'green' | 'magenta' }> = {
  OPEN: { label: 'Chờ xử lý', type: 'red' }, CLEARED: { label: 'Không vi phạm', type: 'green' }, FRAUD: { label: 'Gian lận', type: 'magenta' },
};
// ten truong chi tiet -> nhan tieng Viet (chi tiet cua moi quy tac: backend lib/chong_lam_dung.js, cms jobs/risk.ts)
const FIELD: Record<string, string> = {
  tien: 'Số tiền', soVan: 'Số ván', z: 'Lệch (sigma)', nguong: 'Ngưỡng', dem: 'Đếm', toiDa: 'Tối đa', game: 'Game', gid: 'Mã game',
  maVan: 'Ván', ip: 'IP', fg: 'Vân tay thiết bị', thietBi: 'Thiết bị', tienNap: 'Tiền nạp', daCuoc: 'Đã cược', can: 'Cần cược', soTien: 'Tiền rút',
  soLan: 'Số lần', phut: 'Phút', gio: 'Giờ', lanDau: 'Thấy lần đầu', loai: 'Loại thưởng', daiLy: 'Mã đại lý', lienKet: 'Liên kết',
  cuoc: 'Tiền cược', tra: 'Tiền trả', kyVong: 'Kỳ vọng trả', tyLe: 'Tỷ lệ', doLechMs: 'Độ lệch (ms)', lanTrongPhut: 'Lần trong phút',
};
const MONEY = new Set(['tien', 'tienNap', 'daCuoc', 'can', 'soTien', 'cuoc', 'tra', 'kyVong']);

function Detail({ d }: { d: Record<string, unknown> }) {
  const parts = Object.entries(d).filter(([k, v]) => v != null && v !== '' && FIELD[k]).slice(0, 6);
  if (!parts.length) return <span className="muted">-</span>;
  return (
    <span className="stack-tight">
      {parts.map(([k, v]) => (
        <span key={k}>{FIELD[k]}: {MONEY.has(k) ? <Money v={Number(v)} /> : Array.isArray(v) ? v.join(', ') : String(v)}</span>
      ))}
    </span>
  );
}

const KEYS = ['status', 'rule', 'userId'] as const;

// Risk queue (roadmap P1-D20): what the anti-abuse rules raised, and the admin's decision on each event.
// One tab of the risk page (pages/Risk.tsx), next to the analytics scan of P1-D17 / P1-D19.
export const RISK_EVENTS_LEAD = 'Các mục do quy tắc chống gian lận mở: nhiều tài khoản một thiết bị / IP, thưởng tân thủ lần hai, cược hai cửa, công cụ cược, thắng bất thường, nạp rồi rút mà chơi ít, đại lý chung thiết bị / ngân hàng với thành viên. Mục có giữ rút hiện thành cảnh báo trên lệnh rút của người chơi: người duyệt tích đã kiểm tra rồi vẫn duyệt được, đóng mục ở đây khi đã xử lý. Gian lận: thu hồi toàn bộ hoa hồng đại lý đã sinh từ cược của người chơi này và tắt hoa hồng cho các cược sau.';

export function RiskEventQueue() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const f = useUrlFilters(KEYS, { defaults: { status: 'OPEN' } });
  const q = useQuery({
    queryKey: ['risk-events', f.values, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/risk/events', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const [open, setOpen] = useState<RiskEvent | null>(null);
  const [verdict, setVerdict] = useState<'CLEARED' | 'FRAUD'>('CLEARED');
  const [note, setNote] = useState('');
  const [release, setRelease] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const resolve = useMutation({
    mutationFn: (e: RiskEvent) => api.post<{ clawedStake: string; command: { status: string } | null }>(`/api/admin/risk/events/${e.id}/resolve`,
      { verdict, note, releaseWithdraw: verdict === 'CLEARED' && release }),
    onSuccess: (r, e) => {
      setOpen(null);
      qc.invalidateQueries({ queryKey: ['risk-events'] });
      setDone(verdict === 'FRAUD'
        ? `${e.nickname ?? e.userId}: gian lận. Đã thu hồi hoa hồng đại lý trên ${Number(r.clawedStake).toLocaleString('vi-VN')} tiền cược; cược sau không còn hoa hồng.`
        : `${e.nickname ?? e.userId}: không vi phạm.${r.command ? ' Lệnh mở rút: ' + r.command.status : ''}`);
    },
  });
  const rules = q.data?.rules ?? {};
  const fields: FilterField[] = [
    { key: 'status', label: 'Trạng thái', kind: 'select', options: [['OPEN', 'Chờ xử lý'], ['CLEARED', 'Không vi phạm'], ['FRAUD', 'Gian lận'], ['ALL', 'Tất cả']] },
    { key: 'rule', label: 'Quy tắc', kind: 'select', options: [['', 'Tất cả'], ...Object.entries(rules).map(([k, v]) => [k, v] as const)] },
    { key: 'userId', label: 'Mã người chơi', kind: 'number' },
  ];
  const user = (id: number, nick: string | null) => can('users.view') ? <Link className="cell-link" to={`/users/${id}`}>{nick ?? '#' + id}</Link> : <>{nick ?? '#' + id}</>;
  const columns: ColumnDef<RiskEvent, any>[] = [
    { header: 'Lúc', accessorKey: 'at', cell: (c) => <When v={c.getValue()} /> },
    { header: 'Người chơi', accessorKey: 'nickname', cell: (c) => user(c.row.original.userId, c.row.original.nickname) },
    { header: 'Quy tắc', accessorKey: 'rule', cell: (c) => <>{riskRuleLabel(c.getValue())} <span className="muted">({c.row.original.source === 'game' ? 'hệ game' : 'CMS'})</span></> },
    { header: 'Chi tiết', id: 'detail', enableSorting: false, cell: (c) => <Detail d={c.row.original.detail} /> },
    { header: 'Liên quan', id: 'rel', enableSorting: false, cell: (c) => c.row.original.relatedUsers.length
      ? <span className="stack-tight">{c.row.original.relatedUsers.map((u) => <span key={u.id}>{user(u.id, u.nickname)}</span>)}</span> : <span className="muted">-</span> },
    { header: 'Đã làm', id: 'act', enableSorting: false, cell: (c) => <>{c.row.original.actions.map((a) => <Tag key={a} size="sm" type="gray">{RISK_ACTION_LABEL[a]}</Tag>)}</> },
    { header: 'Rút tiền', id: 'wd', enableSorting: false, cell: (c) => c.row.original.withdrawFrozen ? <Tag size="sm" type="red">Đang giữ</Tag> : <span className="muted">Mở</span> },
    { header: 'Trạng thái', accessorKey: 'status', cell: (c) => {
      const e = c.row.original;
      if (e.status === 'OPEN') {
        return can('risk.review') ? <Button size="sm" kind="tertiary" aria-label={`Xử lý mục rủi ro của ${e.nickname ?? e.userId}`}
          onClick={() => { setOpen(e); setVerdict('CLEARED'); setNote(''); setRelease(false); }}>Xử lý</Button> : <Tag type="red" size="sm">Chờ xử lý</Tag>;
      }
      return (
        <span className="stack-tight">
          <Tag size="sm" type={STATUS[e.status].type}>{STATUS[e.status].label}</Tag>
          <span className="muted"><When v={e.resolvedAt} /></span>
          {e.note ? <span className="muted">{e.note}</span> : null}
          {e.status === 'FRAUD' ? <span className="muted">Thu hồi hoa hồng trên <Money v={e.clawedStake} /> tiền cược</span> : null}
        </span>
      );
    } },
  ];
  const c = q.data?.counts ?? {};
  return (
    <>
      <p className="note">{RISK_EVENTS_LEAD}</p>
      <p className="muted">Chờ xử lý {c.OPEN ?? 0} · Không vi phạm {c.CLEARED ?? 0} · Gian lận {c.FRAUD ?? 0}</p>
      {done ? <InlineNotification kind="success" lowContrast title="Đã xử lý" subtitle={done} onClose={() => setDone(null)} /> : null}
      <FilterBar id="risk" fields={fields} filters={f} />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} rowKey={(r) => r.id}
        empty={{ title: f.active ? 'Không có mục khớp bộ lọc' : 'Không có mục nào chờ xử lý', body: can('risk.config') ? <>Ngưỡng và hành động nằm ở <Link to="/anti-abuse">Chống gian lận</Link>.</> : undefined }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[25, 50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} mục`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      <Modal open={!!open} modalHeading={open ? `Xử lý: ${riskRuleLabel(open.rule)} (${open.nickname ?? open.userId})` : ''}
        primaryButtonText={verdict === 'FRAUD' ? 'Xác nhận gian lận' : 'Không vi phạm'} secondaryButtonText="Hủy" danger={verdict === 'FRAUD'}
        primaryButtonDisabled={resolve.isPending || note.trim().length < 3} onRequestClose={() => setOpen(null)}
        onRequestSubmit={() => open && resolve.mutate(open)}>
        <div className="stack">
          <RadioButtonGroup legendText="Kết luận" name="risk-verdict" valueSelected={verdict} onChange={(v) => setVerdict(v as 'CLEARED' | 'FRAUD')} orientation="vertical">
            <RadioButton id="rv-clear" labelText="Không vi phạm" value="CLEARED" />
            <RadioButton id="rv-fraud" labelText="Gian lận: thu hồi hoa hồng đại lý đã sinh từ người chơi này, cược sau không có hoa hồng" value="FRAUD" />
          </RadioButtonGroup>
          {verdict === 'CLEARED' && open?.withdrawFrozen ? (
            <Checkbox id="rv-release" labelText="Mở lại rút tiền cho người chơi" checked={release} onChange={(_e, { checked }) => setRelease(checked)} />
          ) : null}
          <TextArea id="rv-note" labelText="Lý do" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3} />
          <InlineError error={resolve.error} />
        </div>
      </Modal>
    </>
  );
}
